import React, { memo } from "react";
import { router, usePage } from "@inertiajs/react";
import SmartLink from "@/components/ui/SmartLink";
import AppLogo from "@/components/assets/AppLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { Category } from "@/types";
import CategorySidebar from "@/components/navigation/CategorySidebar";
import { useMobileNavigation } from "@/hooks/use-mobile-navigation";

import SimpleWeatherWidget from "@/components/weather/SimpleWeatherWidget";
import UniversalSearch from "@/components/search/UniversalSearch";
import { useSearchState } from "@/hooks/useSearchState";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import {
  LogOutIcon,
  UserIcon,
  SettingsIcon,
  MenuIcon,
  ChevronDownIcon,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface headerProps {
  user?: {
    id: number;
    name: string;
    email: string;
    image?: string;
  };
  categories: Category[];
}

function HomeHeader(data: headerProps) {
  const { isSearchExpanded, handleSearchExpandChange } = useSearchState();

  // Enhanced mobile navigation
  const {
    isOpen,
    openNavigation,
    closeNavigation,
    handleNavigationItemClick,
    getTouchTargetSize,
  } = useMobileNavigation({
    preventBodyScroll: true,
    enableSwipeGestures: true,
    closeOnOutsideClick: true,
  });

  // We'll use the CategorySidebar component which handles its own category logic

  const handleLogout = async () => {
    try {
      // Get fresh CSRF token before logout
      await fetch("/csrf-token", {
        credentials: "include",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      router.post(
        "/logout",
        {},
        {
          onSuccess: () => {
            // Clear any cached data and force page reload to ensure clean state
            window.location.href = "/";
          },
          onError: (errors) => {
            console.error("Logout error:", errors);
            // If logout fails due to CSRF or any other error, force redirect to home
            // This ensures user is logged out even if there's a session issue
            window.location.href = "/";
          },
        },
      );
    } catch (error) {
      console.error("Logout preparation error:", error);
      // If CSRF fetch fails, force redirect to home to clear everything
      window.location.href = "/";
    }
  };

  const user = {
    name: data.user?.name,
    email: data.user?.email,
  };

  // Helper functions for avatar
  const getAvatarUrl = () => {
    if (data.user?.image) {
      return `/storage/images/${data.user.image}`;
    }
    return "/img/Default.png";
  };

  const getInitials = () => {
    if (!user.name) return "U";
    return user.name
      .split(" ")
      .map((name) => name.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <header className="border-b border-border bg-primary-darker bg-linear-[156deg] from-primary-lighter/70 to-primary-darker fixed w-full top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          {/* Main Header Row */}
          <div className="flex flex-col">
            <div className="order-last px-6 md:hidden">
              <div className="flex flex-row gap-4 items-center justify-between">
                <div
                  className={`my-1.5 flex flex-row gap-4 lg:hidden items-center justify-between transition-all duration-300 ${
                    isSearchExpanded
                      ? "opacity-0 scale-95 pointer-events-none"
                      : "opacity-100 scale-100"
                  }`}
                >
                  <b className="text-xs md:text-sm text-white mt-1">
                    {new Date().toLocaleDateString("id-ID", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </b>
                  <SimpleWeatherWidget />
                </div>
                {/* Center: Search Bar (Desktop) */}
                <div className="flex-1 w-full absolute right-0">
                  <UniversalSearch
                    compact={true}
                    placeholder="Cari berita..."
                    iconSize={5}
                    btnClassName="p-2 mr-1  bg-white/10 rounded-lg hover:bg-white/20 "
                    onExpandChange={handleSearchExpandChange}
                  />
                </div>
              </div>
            </div>
            <div className=" flex justify-between items-center">
              {/* Logo */}
              <div className="flex flex-col">
                <SmartLink href="/" prefetchStrategy="hover" className="">
                  <AppLogo className="h-16" />
                </SmartLink>
              </div>

              {/* Right Side: Desktop User Menu & Mobile Menu */}
              <div className="flex items-center gap-4">
                <div className=" items-center relative px-14">
                  <div
                    className={`hidden md:flex flex-row gap-4 items-center justify-between transition-all duration-300 ${
                      isSearchExpanded
                        ? "opacity-0 scale-95 pointer-events-none"
                        : "opacity-100 scale-100"
                    }`}
                  >
                    <b className="text-sm text-white mt-1">
                      {new Date().toLocaleDateString("id-ID", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </b>
                    <SimpleWeatherWidget />
                  </div>
                  <div className="hidden md:flex absolute top-0 right-0">
                    <UniversalSearch
                      compact={true}
                      placeholder="Cari berita..."
                      iconSize={5}
                      btnClassName="p-2 bg-white/10 rounded-lg hover:bg-white/20"
                      onExpandChange={handleSearchExpandChange}
                    />
                  </div>
                </div>

                {/* Desktop User Menu */}
                <div className="hidden lg:flex items-center space-x-4">
                  {user.name ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="flex items-center space-x-3 p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20">
                          <Avatar className="h-8 w-8">
                            <AvatarImage
                              src={getAvatarUrl()}
                              alt={user?.name || "User"}
                            />
                            <AvatarFallback className="bg-white/20 text-white text-sm font-medium">
                              {getInitials()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="hidden md:block text-left">
                            <div className="text-sm font-medium text-white">
                              {user.name}
                            </div>
                            <div className="text-xs text-white/70">
                              {user.email}
                            </div>
                          </div>
                          <ChevronDownIcon className="h-4 w-4 text-white/70" />
                        </button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent
                        className="w-56 bg-white dark:bg-black dark:text-white border-0"
                        align="end"
                        forceMount
                      >
                        <DropdownMenuLabel className="font-normal">
                          <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium leading-none">
                              {user.name}
                            </p>
                            <p className="text-xs leading-none text-muted-foreground">
                              {user.email}
                            </p>
                          </div>
                        </DropdownMenuLabel>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem asChild>
                          <a
                            href="/dashboard"
                            className="flex items-center cursor-pointer"
                          >
                            <SettingsIcon className="mr-2 h-4 w-4" />
                            <span>Dashboard</span>
                          </a>
                        </DropdownMenuItem>

                        <DropdownMenuItem asChild>
                          <a
                            href={`/profile/${data.user?.id}/edit`}
                            className="flex items-center cursor-pointer"
                          >
                            <UserIcon className="mr-2 h-4 w-4" />
                            <span>Profile</span>
                          </a>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                          onClick={handleLogout}
                          className="flex items-center cursor-pointer text-red-600 focus:text-red-600"
                        >
                          <LogOutIcon className="mr-2 h-4 w-4" />
                          <span>Logout</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <div className="flex items-center space-x-2 text-sm">
                      <SmartLink
                        href="/login"
                        prefetchStrategy="hover"
                        className="text-white border-b-2 border-transparent hover:border-white py-1"
                      >
                        Masuk
                      </SmartLink>
                      <span className="text-gray-400">|</span>
                      <SmartLink
                        href="/register"
                        prefetchStrategy="hover"
                        className="text-white border-b-2 border-transparent hover:border-white py-1"
                      >
                        Daftar
                      </SmartLink>
                    </div>
                  )}
                </div>

                {/* Theme Toggle */}
                <ThemeToggle className="hidden lg:flex" />

                {/* Mobile Menu */}
                <div className="lg:hidden">
                  <Sheet
                    open={isOpen}
                    onOpenChange={(open) =>
                      open ? openNavigation() : closeNavigation()
                    }
                  >
                    <SheetTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="mt-0 text-white hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white min-h-[48px] min-w-[48px] touch-manipulation"
                        data-navigation-trigger
                        aria-label="Open menu"
                        aria-expanded={isOpen}
                        style={getTouchTargetSize()}
                      >
                        <MenuIcon className="h-6 w-6" />
                        <span className="sr-only">Toggle menu</span>
                      </Button>
                    </SheetTrigger>
                    <SheetContent
                      side="right"
                      className="w-full sm:w-[320px] p-0 bg-lg-background/10 dark:bg-dr-background/30 backdrop-blur-lg [&>button:first-of-type]:cursor-pointer [&>button:first-of-type]:text-white [&>button:first-of-type]:p-3 [&>button:first-of-type]:rounded-full [&>button:first-of-type]:hover:bg-red-500/10 [&>button:first-of-type]:min-h-[48px] [&>button:first-of-type]:min-w-[48px] [&>button:first-of-type]:[&>svg]:w-6 [&>button:first-of-type]:[&>svg]:h-6"
                      data-navigation-content
                    >
                      <div className="h-full flex flex-col">
                        <SheetHeader className="px-4 py-4">
                          <SheetTitle className="text-lg font-bold text-left">
                            <SmartLink
                              href="/"
                              prefetchStrategy="hover"
                              className=""
                            >
                              <AppLogo className="h-16" />
                            </SmartLink>
                          </SheetTitle>
                        </SheetHeader>

                        <div className="flex-1 overflow-y-auto px-4 py-4 h-full">
                          {/* Categories Section */}
                          <CategorySidebar
                            fallbackCategories={data.categories}
                            onItemClick={handleNavigationItemClick}
                            showHeader={true}
                            showLoadingState={true}
                            showErrorState={true}
                            className="mobile-optimized"
                          />
                        </div>
                        {/* User Section */}
                        <div className="bg-muted/50 px-4 py-4 mt-auto rounded-t-2xl ml-4 mr-4 md:mx-0 touch-manipulation">
                          {user.name && (
                            <div className="space-y-3">
                              {/* User Profile Section */}
                              <div className="flex items-center space-x-3 p-3 bg-background rounded-lg bg-lg-background/20 dark:bg-dr-background/30">
                                <Avatar className="h-10 w-10">
                                  <AvatarImage
                                    src={getAvatarUrl()}
                                    alt={user.name}
                                  />
                                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                                    {getInitials()}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate text-white">
                                    {user.name}
                                  </div>
                                  <div className="text-xs text-gray-300 truncate">
                                    {user.email}
                                  </div>
                                </div>
                              </div>

                              {/* Menu Items */}
                              <div className="space-y-1 text-gray-300 dark:text-gray-400">
                                <a
                                  href="/dashboard"
                                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-accent transition-colors w-full"
                                  onClick={() => closeNavigation()}
                                >
                                  <SettingsIcon
                                    size={18}
                                    className="text-primary"
                                  />
                                  <span className="text-sm font-semibold">
                                    Dashboard
                                  </span>
                                </a>

                                <SmartLink
                                  href={`/profile/${data.user?.id}/edit`}
                                  prefetchStrategy="hover"
                                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-accent transition-colors w-full"
                                  onClick={() => closeNavigation()}
                                >
                                  <UserIcon
                                    size={18}
                                    className="text-primary"
                                  />
                                  <span className="text-sm font-semibold">
                                    Profile
                                  </span>
                                </SmartLink>
                              </div>
                            </div>
                          )}
                          {/* Theme Toggle */}
                          <div className="flex items-center justify-between pt-2">
                            <div className="flex gap-2 items-center text-gray-300 dark:text-gray-400">
                              {user.name ? (
                                <>
                                  <button
                                    onClick={() => {
                                      handleLogout();
                                      closeNavigation();
                                    }}
                                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-destructive/10 transition-colors text-destructive w-full text-left"
                                  >
                                    <LogOutIcon
                                      size={18}
                                      className="text-primary"
                                    />
                                    <span className="text-sm font-semibold pb-0.5">
                                      Logout
                                    </span>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <SmartLink
                                    href="/login"
                                    prefetchStrategy="hover"
                                    className="flex items-center justify-center py-2 px-6 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                                    onClick={() => closeNavigation()}
                                  >
                                    Masuk
                                  </SmartLink>
                                  <SmartLink
                                    href="/register"
                                    prefetchStrategy="hover"
                                    className="flex items-center justify-center py-2 px-6 border border-border rounded-lg hover:bg-accent transition-colors text-sm font-medium"
                                    onClick={() => closeNavigation()}
                                  >
                                    Daftar
                                  </SmartLink>
                                </>
                              )}
                            </div>
                            <ThemeToggle />
                          </div>
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div className="h-[140px] md:h-[90px]"></div>
    </>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(HomeHeader, (prevProps, nextProps) => {
  return (
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.user?.name === nextProps.user?.name &&
    prevProps.user?.email === nextProps.user?.email &&
    prevProps.user?.image === nextProps.user?.image &&
    prevProps.categories?.length === nextProps.categories?.length
  );
});
